import { PointerEvent, useCallback, useEffect, useRef, useState } from 'react';

const MOBILE = 768;

export function shouldDismissSheet(dy: number, vy: number): boolean {
  if (dy < 0) return false;
  return dy > 96 || (dy > 52 && vy > 0.8);
}

export function canStartSwipe(target: EventTarget | null, sheet: HTMLElement | null): boolean {
  if (!(target instanceof Element) || !sheet) return false;
  if (target.closest('button, a, textarea, input, select, [role="button"]')) return false;
  if (target.closest('.assistant-dock__composer')) return false;
  if (target.closest('.assistant-dock__grab, .assistant-dock__bar')) return true;
  const scroller = target.closest('.assistant-dock__thread, .assistant-dock__history');
  return Boolean(scroller instanceof HTMLElement && scroller.scrollTop <= 0);
}

function fromGrab(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('.assistant-dock__grab, .assistant-dock__bar'));
}

export function useDockSwipe(onClose: () => void, enabled: boolean) {
  const sheetRef = useRef<HTMLElement>(null);
  const drag = useRef<{ id: number; x: number; y: number; t: number; armed: boolean } | null>(null);
  const [shift, setShift] = useState(0);
  const [dragging, setDragging] = useState(false);

  const reset = useCallback(() => {
    drag.current = null;
    setShift(0);
    setDragging(false);
  }, []);

  useEffect(() => {
    if (!enabled) reset();
  }, [enabled, reset]);

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    if (!enabled) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (window.innerWidth > MOBILE) return;
    if (!canStartSwipe(event.target, sheetRef.current)) return;
    const grab = fromGrab(event.target);
    drag.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      t: Date.now(),
      armed: grab
    };
    if (grab) {
      setDragging(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    const next = drag.current;
    if (!next || next.id !== event.pointerId) return;
    const dy = event.clientY - next.y;
    const dx = event.clientX - next.x;
    if (!next.armed) {
      if (dy < 8 || Math.abs(dx) >= dy) return;
      next.armed = true;
      setDragging(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    setShift(Math.max(0, dy));
  };

  const onPointerUp = (event: PointerEvent<HTMLElement>) => {
    const next = drag.current;
    if (!next || next.id !== event.pointerId) return;
    const dy = Math.max(0, event.clientY - next.y);
    const dt = Math.max(1, Date.now() - next.t);
    const close = next.armed && shouldDismissSheet(dy, dy / dt);
    reset();
    if (close) onClose();
  };

  return {
    sheetRef,
    dragging,
    shift,
    onPointerDown,
    onPointerMove,
    onPointerUp
  };
}
